import { PageHeader } from '@src/components/shared/PageHeader';
import { Card, CardContent } from '@src/components/ui/card';
import { errorMessage } from '@src/lib/errors';
import { resources } from '@src/resources';
import { fetchStats, scopeLabel, type StatsRow, type StatsWindow } from '@src/services/opsService';
import { useQuery } from '@tanstack/react-query';

/** Fixed reporting window for this screen -- matches the prototype's static "Last 24 hours". */
const STATS_WINDOW: StatsWindow = '24h';

function formatMs(durationMs: number): string {
  return `${durationMs.toLocaleString()}ms`;
}

interface LatencyRowProps {
  row: StatsRow;
  /** Largest p95 across all rows, used to scale every bar to the same axis. */
  maxP95DurationMs: number;
}

function LatencyRow({ row, maxP95DurationMs }: LatencyRowProps) {
  const p95Pct = maxP95DurationMs > 0 ? (row.p95DurationMs / maxP95DurationMs) * 100 : 0;
  const p50Pct = maxP95DurationMs > 0 ? (row.p50DurationMs / maxP95DurationMs) * 100 : 0;

  return (
    <div className="flex items-center gap-3">
      <div className="w-[170px] shrink-0 text-xs text-muted-foreground">{scopeLabel(row.scope)}</div>

      <div className="relative h-[18px] flex-1 overflow-hidden rounded bg-muted">
        <div className="h-full rounded bg-primary" style={{ width: `${p95Pct}%` }} />
        <div
          className="absolute inset-y-0 w-0.5"
          style={{ left: `${p50Pct}%`, backgroundColor: 'rgba(255,255,255,0.65)' }}
        />
      </div>

      <div className="w-[150px] shrink-0 text-right text-[11.5px] tabular-nums text-muted-foreground">
        p50 {formatMs(row.p50DurationMs)} &middot; p95 {formatMs(row.p95DurationMs)}
      </div>
    </div>
  );
}

export function Latency() {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['ops-stats', STATS_WINDOW],
    queryFn: () => fetchStats({ window: STATS_WINDOW }),
  });

  const rows = [...(data?.items ?? [])].sort((a, b) => b.p95DurationMs - a.p95DurationMs);
  const maxP95DurationMs = Math.max(0, ...rows.map((row) => row.p95DurationMs));

  return (
    <div>
      <PageHeader title={resources.pages.latency.title} subtitle={resources.pages.latency.subtitle} />
      <Card>
        <CardContent className="p-[18px]">
          <div className="mb-0.5 text-[12.5px] font-semibold text-foreground">
            {resources.pages.latency.chartTitle}
          </div>
          <div className="mb-4 text-[11px] text-muted-foreground">{resources.pages.latency.chartSubtitle}</div>

          {isLoading && <p className="text-sm text-muted-foreground">{resources.common.loading}</p>}
          {isError && (
            <div className="text-sm text-status-critical">
              <p>{resources.pages.latency.loadError}</p>
              <p className="mt-1 font-mono text-xs text-muted-foreground">{errorMessage(error)}</p>
            </div>
          )}
          {!isLoading && !isError && rows.length === 0 && (
            <p className="text-sm text-muted-foreground">{resources.pages.latency.empty}</p>
          )}

          {!isLoading && !isError && rows.length > 0 && (
            <div className="flex flex-col gap-3.5">
              {rows.map((row) => (
                <LatencyRow key={row.scope} row={row} maxP95DurationMs={maxP95DurationMs} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
